import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../../axios/api";
import ClipLoader from "react-spinners/ClipLoader";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "../../assets/NewFiles/Css/UpdateGallery.css";
import DatePicker from "react-datepicker";
import { FaClock, FaMapMarkerAlt, FaBuilding, FaArrowLeft } from 'react-icons/fa';
import Loader from "../common/Loader";
import Button from "../common/styled components/Button";

const schema = yup.object().shape({
  image: yup.mixed().test("required", "Image is required", (value) => {
    return value && value[0];
  }),
  imageCaption: yup.string().required("Image Caption is required"),
  imageDescription: yup.string().required("Image Description is required"),
  publishFromDate: yup
    .date("Enter a valid Date")
    .required("Publish From Date is required")
    .typeError("Please enter a valid Publish From Date"),
  publishTillDate: yup
    .date()
    .required("Publish Till Date is required")
    .min(yup.ref("publishFromDate"), "End date must be after start date")
    .typeError("Please enter a valid Publish Till Date"),
});

const ProjectGalleryIndividual = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [gallery, setGallery] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);

  // Form states
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
  });

  // Edit form states
  const [editImageCaption, setEditImageCaption] = useState("");
  const [editImageDescription, setEditImageDescription] = useState("");
  const [editPublishFromDate, setEditPublishFromDate] = useState("");
  const [editPublishTillDate, setEditPublishTillDate] = useState("");
  const [editImageFile, setEditImageFile] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [project, setProject] = useState("");

  const [publishFromDate, setPublishFromDate] = React.useState(null);
  const [publishTillDate, setPublishTillDate] = React.useState(null);

  const fetchPosts = async () => {
    setIsLoading(true); // Set loading state to true
    try {
      const response = await api.get(
        `/api/project/getProjectGalleryDetails/${id}`
      );
      setProject(response.data.project);
      setGallery(response.data.galleryImages);
    } catch (error) {
      console.error("Error fetching posts:", error);
      toast.error("Failed to load gallery data");
    } finally {
      setIsLoading(false); // Set loading state to false after fetching
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  function convertToYYYYMMDD(dateString) {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  // Handle new image upload
  const onSubmit = async (data) => {
    if (!data.image || !data.image[0]) {
      toast.error("Please select an image");
      return;
    }

    setIsSubmitting(true);
    setIsLoading(true); // Show loader during submission
    
    const formData = new FormData();
    formData.append("projectGalleryImage", data.image[0]);
    formData.append("imageCaption", data.imageCaption);
    formData.append("imageDescription", data.imageDescription);
    formData.append("publishFromDate", convertToYYYYMMDD(data.publishFromDate));
    formData.append("publishTillDate", convertToYYYYMMDD(data.publishTillDate));
    formData.append("projectId", project.project_id);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Unauthorized: No token found");
        return;
      }

      const response = await api.post(
        `/api/project/uploadProjectGallery`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 201) {
        toast.success("Image upload successful");
        await fetchPosts(); // Refresh the gallery data
        reset();
        setOpenModal(false);
        setPublishFromDate(null);
        setPublishTillDate(null);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to upload image");
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  // Validate edit form
  const validateEditForm = () => {
    const errors = {};
    if (!editImageCaption) errors.imageCaption = "Image Caption is required";
    if (!editImageDescription)
      errors.imageDescription = "Image Description is required";
    if (!editPublishFromDate)
      errors.publishFromDate = "Publish From Date is required";
    if (!editPublishTillDate)
      errors.publishTillDate = "Publish Till Date is required";
    if (
      editPublishFromDate &&
      editPublishTillDate &&
      new Date(editPublishTillDate) <= new Date(editPublishFromDate)
    ) {
      errors.publishTillDate = "End date must be after start date";
    }
    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle image edit and save
  const handleEditSubmit = async (index) => {
    if (!validateEditForm()) return;

    const imageId = gallery[index].id;
    const formData = new FormData();
    
    setIsLoading(true); // Show loader during edit submission
    
    if (editImageFile) {
      // Changed from "image" to "projectGalleryImage" as requested
      formData.append("projectGalleryImage", editImageFile);
    }
    formData.append("imageCaption", editImageCaption);
    formData.append("imageDescription", editImageDescription);
    formData.append("publishFromDate", convertToYYYYMMDD(editPublishFromDate));
    formData.append("publishTillDate", convertToYYYYMMDD(editPublishTillDate));
    formData.append("projectId", id);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Unauthorized: No token found");
        return;
      }

      const response = await api.post(
        `/api/project/updateProjectGallery/${imageId}`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 200) {
        toast.success("Image updated successfully");
        setEditingIndex(null);
        await fetchPosts(); // Refresh gallery data after update
      }
    } catch (error) {
      console.error("Error updating image:", error);
      toast.error("Failed to update image");
    } finally {
      setIsLoading(false);
    }
  };

  // Open delete confirmation dialog
  const openDeleteConfirm = (index) => {
    setImageToDelete(index);
    setDeleteConfirmOpen(true);
  };

  // Close delete confirmation dialog
  const closeDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setImageToDelete(null);
  };

  // Confirm and execute delete
  const confirmDelete = async () => {
    if (imageToDelete === null) return;
    
    const imageId = gallery[imageToDelete].id;
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Unauthorized: No token found");
        return;
      }

      const response = await api.delete(
        `/api/project/DeleteGalleryImage/${imageId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 200) {
        toast.success("Image deleted successfully");
        await fetchPosts(); // Refresh gallery data after deletion
      }
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error("Failed to delete image");
    } finally {
      setIsLoading(false);
      closeDeleteConfirm();
    }
  };

  const handleEdit = (index) => {
    const image = gallery[index];
    setEditImageCaption(image.imageCaption);
    setEditImageDescription(image.imageDescription);
    setEditPublishFromDate(
      new Date(image.publishFromDate).toISOString().split("T")[0]
    );
    setEditPublishTillDate(
      new Date(image.publishTillDate).toISOString().split("T")[0]
    );
    setEditingIndex(index);
    setEditErrors({});
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setEditImageCaption("");
    setEditImageDescription("");
    setEditPublishFromDate("");
    setEditPublishTillDate("");
    setEditImageFile(null);
    setEditErrors({});
  };

  const handleCloseModal = () => {
    reset();
    setPublishFromDate(null);
    setPublishTillDate(null);
    setOpenModal(false);
  };

  const goBack = () => {
    navigate(-1); // Navigate to previous page
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="photo-upload-container">
        <ToastContainer />
        {isLoading && (
          <div className="loading-overlay fixed inset-0 bg-white bg-opacity-70 z-50 flex justify-center items-center">
            <Loader />
          </div>
        )}
        <div className={`transition-opacity duration-300 ${isLoading ? 'opacity-60 pointer-events-none' : 'opacity-100'}`}>
          {/* Back button */}
          <Button onClick={goBack}>
            <FaArrowLeft className="mr-2" />
            <span>Back to Projects</span>
          </Button>

          {/* Project Info Header */}
          <div className="flex flex-wrap gap-6 items-center mb-6 bg-gray-200 p-4 rounded-xl shadow-md">
            <h2 className="w-full text-center text-2xl font-bold mb-2">{project?.name || ""}</h2>
            <div className="flex items-center text-lg">
              <FaMapMarkerAlt className="text-green-700 mr-2" />
              <span className="font-bold text-gray-800 mr-2" >Venue:</span> 
              <span  className="text-black">{project?.address || ""}</span>
            </div>
            <div className="flex items-center text-lg">
              <FaClock className="text-green-700 mr-2" />
              <span className="font-bold text-gray-800 mr-2">Duration:</span> 
              <span  className="text-black">{project?.startDate || ""} - {project?.endDate || ""}</span>
            </div>
            <div className="flex items-center text-lg">
              <FaBuilding className="text-green-700 mr-2" />
              <span className="font-bold text-gray-800 mr-2">Organized By:</span> 
              <span  className="text-black">{project?.organizationName || ""}</span>
            </div>
          </div>

          {/* Add Image Button */}
          <div className="flex items-center mb-6">
            <Button onClick={() => setOpenModal(true)}>
              Add Gallery Image
            </Button>
          </div>

          {/* Upload Modal */}
          <Dialog
            open={openModal}
            onClose={handleCloseModal}
            maxWidth="sm"
            keepMounted={false}
            disableEnforceFocus
            fullWidth
            aria-labelledby="gallery-dialog-title"
          >
            <DialogTitle>
              Add New Gallery Image
              <IconButton
                onClick={handleCloseModal}
                style={{ position: "absolute", right: 8, top: 8 }}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <p className="text-sm mb-4">
                NOTE: fields marked with <span className="text-red-500">*</span>{" "}
                are mandatory to input.
              </p>
              <form>
                <div className="form-group mb-4">
                  <label className="block mb-2 font-medium">
                    Upload Image <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    {...register("image")}
                    className={`w-full p-2 border rounded ${errors.image ? "border-red-500" : "border-gray-300"}`}
                  />
                  {errors.image && (
                    <p className="text-red-500 text-sm mt-1">{errors.image.message}</p>
                  )}
                </div>
                <div className="form-group mb-4">
                  <label className="block mb-2 font-medium">
                    Image Caption <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register("imageCaption")}
                    className={`w-full p-2 border rounded ${errors.imageCaption ? "border-red-500" : "border-gray-300"}`}
                  />
                  {errors.imageCaption && (
                    <p className="text-red-500 text-sm mt-1">{errors.imageCaption.message}</p>
                  )}
                </div>
                <div className="form-group mb-4">
                  <label className="block mb-2 font-medium">
                    Image Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    {...register("imageDescription")}
                    className={`w-full p-2 border rounded ${errors.imageDescription ? "border-red-500" : "border-gray-300"}`}
                    rows={4}
                  />
                  {errors.imageDescription && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.imageDescription.message}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block mb-2 font-medium">
                      Publish From Date <span className="text-red-500">*</span>
                    </label>
                    <DatePicker
                      selected={publishFromDate}
                      onChange={(date) => {
                        setPublishFromDate(date);
                        setValue("publishFromDate", date);
                      }}
                      dateFormat="yyyy-MM-dd"
                      className={`w-full p-2 border rounded ${errors.publishFromDate ? "border-red-500" : "border-gray-300"}`}
                    />
                    {errors.publishFromDate && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.publishFromDate.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block mb-2 font-medium">
                      Publish Till Date <span className="text-red-500">*</span>
                    </label>
                    <DatePicker
                      selected={publishTillDate}
                      onChange={(date) => {
                        setPublishTillDate(date);
                        setValue("publishTillDate", date);
                      }}
                      dateFormat="yyyy-MM-dd"
                      className={`w-full p-2 border rounded ${errors.publishTillDate ? "border-red-500" : "border-gray-300"}`}
                    />
                    {errors.publishTillDate && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.publishTillDate.message}
                      </p>
                    )}
                  </div>
                </div>
              </form>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseModal} variant="cancel">Cancel</Button>
              <Button
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <ClipLoader color="white" size="20" />
                ) : (
                  "Upload Image"
                )}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Modal */}
          <Dialog
            open={deleteConfirmOpen}
            onClose={closeDeleteConfirm}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>
              Confirm Delete
              <IconButton
                onClick={closeDeleteConfirm}
                style={{ position: "absolute", right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <p className="mb-4">Are you sure you want to delete this image? This action cannot be undone.</p>
              {imageToDelete !== null && gallery[imageToDelete] && (
                <div className="text-center my-4">
                  <img 
                    src={gallery[imageToDelete].imageUrl} 
                    alt={gallery[imageToDelete].imageCaption} 
                    className="max-w-full h-auto max-h-40 mx-auto rounded-lg shadow-md"
                  />
                  <p className="font-bold mt-2">{gallery[imageToDelete].imageCaption}</p>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDeleteConfirm} variant="gray">Cancel</Button>
              <Button 
                onClick={confirmDelete} 
                variant="cancel" 
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </div>

        {/* Gallery Display */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {gallery.length === 0 ? (
            <div className="col-span-full text-center py-12 bg-gray-100 rounded-lg">
              <p className="text-xl text-gray-500">No images available</p>
            </div>
          ) : (
            gallery.map((image, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden gallery-item-dashboard">
                <div className="h-48 overflow-hidden">
                  <img 
                    src={image.imageUrl} 
                    alt={image.imageName} 
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <div className="p-4">
                  {editingIndex === index ? (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">Image Caption</label>
                        <input
                          type="text"
                          value={editImageCaption}
                          onChange={(e) => setEditImageCaption(e.target.value)}
                          className={`w-full p-2 border rounded ${editErrors.imageCaption ? "border-red-500" : "border-gray-300"}`}
                        />
                        {editErrors.imageCaption && (
                          <p className="text-red-500 text-xs mt-1">{editErrors.imageCaption}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Image Description</label>
                        <textarea
                          value={editImageDescription}
                          onChange={(e) => setEditImageDescription(e.target.value)}
                          className={`w-full p-2 border rounded ${editErrors.imageDescription ? "border-red-500" : "border-gray-300"}`}
                          rows={3}
                        />
                        {editErrors.imageDescription && (
                          <p className="text-red-500 text-xs mt-1">{editErrors.imageDescription}</p>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium mb-1">From Date</label>
                          <DatePicker
                            selected={new Date(editPublishFromDate)}
                            onChange={(date) => setEditPublishFromDate(date)}
                            className={`w-full p-2 border rounded ${editErrors.publishFromDate ? "border-red-500" : "border-gray-300"}`}
                          />
                          {editErrors.publishFromDate && (
                            <p className="text-red-500 text-xs mt-1">{editErrors.publishFromDate}</p>
                          )}
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-1">Till Date</label>
                          <DatePicker
                            selected={new Date(editPublishTillDate)}
                            onChange={(date) => setEditPublishTillDate(date)}
                            className={`w-full p-2 border rounded ${editErrors.publishTillDate ? "border-red-500" : "border-gray-300"}`}
                          />
                          {editErrors.publishTillDate && (
                            <p className="text-red-500 text-xs mt-1">{editErrors.publishTillDate}</p>
                          )}
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Change Image (Optional)</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setEditImageFile(e.target.files[0])}
                          className="w-full p-2 border rounded border-gray-300"
                        />
                      </div>
                      
                      <div className="flex justify-end pt-3">
                        <Button onClick={handleCancel} variant="cancel" >Cancel</Button>
                        <Button onClick={() => handleEditSubmit(index)} >Save</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div>
                        <span className="font-bold text-gray-700">Image Caption:</span>
                        <p>{image.imageCaption}</p>
                      </div>
                      
                      <div>
                        <span className="font-bold text-gray-700">Image Description:</span>
                        <p className="text-sm">{image.imageDescription}</p>
                      </div>
                      
                      <div className="text-sm">
                        <div>
                          <span className="font-bold text-gray-700">From:</span>
                          <p>{image.publishFromDate}</p>
                        </div>
                      </div>

                      <div>
                          <span className="font-bold text-gray-700">Till:</span>
                          <p>{image.publishTillDate}</p>
                        </div>
                      
                      <div className="flex justify-end pt-3">
                        <Button variant="cancel" onClick={() => openDeleteConfirm(index)}> Delete</Button>
                        <Button onClick={() => handleEdit(index)} >Edit</Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectGalleryIndividual;